import { useMemo, useState } from 'react';
import { Plus, Activity, Cpu } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  KeyValueGrid,
  MetricCard,
  Select,
  Table,
  Tabs,
  type Column,
  type TabItem,
} from '@/components';
import { useData, useUI } from '@/data/stores';
import { demoTimestamp, downloadJson } from '@/lib/export';
import { fmtRelative, fmtUTC, fmtNumber } from '@/lib/format';
import { PURDUE_LEVELS } from '@/domain';
import type { OTDevice, PolicyRule } from '@/types';
import { JDL_LEVELS } from '@/domain/jdl-fusion';
import './ot.css';

export function OTMonitoringView() {
  const { otDevices, policies, sensors } = useData();
  const refreshBaselines = useData((s) => s.refreshBaselines);
  const deploySensor = useData((s) => s.deploySensor);
  const notify = useUI((s) => s.notifyAction);

  const counts = useMemo(() => {
    const byPurdue: Record<number, number> = {};
    let withCves = 0;
    let online = 0;
    let degraded = 0;
    for (const d of otDevices) {
      byPurdue[d.purdue_level] = (byPurdue[d.purdue_level] ?? 0) + 1;
      if (d.cves.length > 0) withCves += 1;
      if (d.status === 'online') online += 1;
      if (d.status === 'degraded') degraded += 1;
    }
    return { byPurdue, withCves, online, degraded, total: otDevices.length };
  }, [otDevices]);

  const tabs: TabItem[] = [
    { key: 'devices', label: 'Discovered devices', badge: <span>{otDevices.length}</span>, content: <DevicesPanel /> },
    { key: 'baselines', label: 'Baselines & anomalies', content: <BaselinesPanel /> },
    { key: 'policies', label: 'Policy engine', badge: <span>{policies.length}</span>, content: <PoliciesPanel /> },
    { key: 'sensors', label: 'Sensor appliances', badge: <span>{sensors.length}</span>, content: <SensorsPanel /> },
    { key: 'fusion', label: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Cpu size={12} strokeWidth={1.5} /> JDL fusion</span>, content: <FusionPanel /> },
  ];

  return (
    <div className="iris-page">
      <header className="iris-page-header">
        <div>
          <h1>OT Monitoring & Active Defence</h1>
          <p className="caption">
            Layer 3 - Deep packet inspection across 18+ industrial protocols, behavioural
            baselines, autonomous enforcement, and cyber-physical correlation.
          </p>
        </div>
        <div className="iris-page-actions">
          <Button
            icon={<Activity size={14} strokeWidth={1.5} />}
            onClick={() => {
              const refreshed = refreshBaselines();
              notify({
                kind: 'success',
                title: 'Baselines refreshed',
                body: `${refreshed} behavioural baselines pulled and anomaly counters reconciled.`,
              });
            }}
          >
            Pull baselines
          </Button>
          <Button
            variant="primary"
            icon={<Plus size={14} strokeWidth={1.5} />}
            onClick={() => {
              const deployed = deploySensor();
              downloadJson(`iris-sensor-deployment-plan-${demoTimestamp()}.json`, {
                generated_at: new Date().toISOString(),
                recommended_mode: 'hybrid',
                deployed_sensor: deployed,
                proposed_sensor: {
                  form_factor: 'DIN-rail industrial PC',
                  deployment_mode: 'SPAN perimeter + inline critical conduits',
                  target_sites: sensors
                    .filter((s) => s.status !== 'online')
                    .map((s) => s.site),
                },
                policy_scope: policies.filter((p) => p.enabled).map((p) => p.rule_id),
              });
              notify({
                kind: deployed ? 'success' : 'warning',
                title: 'Sensor deployment plan created',
                body: deployed
                  ? `${deployed.name} is now online and a deployment package was downloaded.`
                  : 'No target asset was available; deployment package downloaded for review.',
              });
            }}
          >
            Deploy sensor
          </Button>
        </div>
      </header>

      <section className="iris-page-metrics">
        <MetricCard label="Devices" value={counts.total} caption="discovered" />
        <MetricCard label="Online" value={counts.online} status="var(--status-operational)" />
        <MetricCard label="Degraded" value={counts.degraded} status="var(--status-degraded)" />
        <MetricCard
          label="With CVEs"
          value={counts.withCves}
          status="var(--status-degraded)"
          caption="exposed to known issues"
        />
        <MetricCard label="Active policies" value={policies.filter((p) => p.enabled).length} />
        <MetricCard label="Sensors online" value={sensors.filter((s) => s.status === 'online').length} />
      </section>

      <Tabs items={tabs} />
    </div>
  );
}

function DevicesPanel() {
  const otDevices = useData((s) => s.otDevices);
  const [purdue, setPurdue] = useState<string>('');
  const [zone, setZone] = useState<string>('');
  const [vendor, setVendor] = useState<string>('');
  const filtered = useMemo(() => {
    return otDevices.filter((d) => {
      if (purdue !== '' && d.purdue_level !== Number(purdue)) return false;
      if (zone && d.zone !== zone) return false;
      if (vendor && d.vendor !== vendor) return false;
      return true;
    });
  }, [otDevices, purdue, zone, vendor]);
  const zones = Array.from(new Set(otDevices.map((d) => d.zone))).sort();
  const vendors = Array.from(new Set(otDevices.map((d) => d.vendor))).sort();

  const cols: Column<OTDevice>[] = [
    {
      key: 'name',
      header: 'Device',
      sortValue: (d) => d.name,
      render: (d) => (
        <div>
          <div>{d.name}</div>
          <div className="caption mono">
            {d.vendor} · {d.model}
          </div>
        </div>
      ),
    },
    { key: 'asset', header: 'Site', mono: true, sortValue: (d) => d.asset_id, render: (d) => d.asset_id },
    {
      key: 'type',
      header: 'Type',
      sortValue: (d) => d.device_type,
      render: (d) => <Badge color="var(--status-info)">{d.device_type}</Badge>,
    },
    { key: 'purdue', header: 'L', mono: true, align: 'right', sortValue: (d) => d.purdue_level, render: (d) => `L${d.purdue_level}` },
    { key: 'ip', header: 'IP', mono: true, sortValue: (d) => d.ip, render: (d) => d.ip },
    { key: 'firmware', header: 'Firmware', mono: true, sortValue: (d) => d.firmware, render: (d) => d.firmware },
    {
      key: 'protocols',
      header: 'Protocols',
      render: (d) => <span className="mono caption">{d.protocols.join(', ')}</span>,
    },
    { key: 'zone', header: 'Zone', sortValue: (d) => d.zone, render: (d) => d.zone },
    {
      key: 'cves',
      header: 'CVEs',
      mono: true,
      align: 'right',
      sortValue: (d) => d.cves.length,
      render: (d) =>
        d.cves.length > 0 ? <Badge color="var(--status-degraded)">{d.cves.length}</Badge> : '-',
    },
    {
      key: 'status',
      header: 'Status',
      render: (d) => (
        <Badge
          color={
            d.status === 'online'
              ? 'var(--status-operational)'
              : d.status === 'degraded'
                ? 'var(--status-degraded)'
                : 'var(--status-critical)'
          }
        >
          {d.status}
        </Badge>
      ),
    },
    {
      key: 'last',
      header: 'Last seen',
      sortValue: (d) => d.last_seen,
      render: (d) => <span className="caption">{fmtRelative(d.last_seen)}</span>,
    },
  ];

  return (
    <div className="iris-ot-grid">
      <Card>
        <div className="iris-ot-filters">
          <Select
            label="Purdue level"
            value={purdue}
            onChange={(e) => setPurdue(e.target.value)}
            options={[
              { value: '', label: 'All' },
              ...PURDUE_LEVELS.map((p) => ({ value: String(p.level), label: p.name })),
            ]}
          />
          <Select
            label="Zone"
            value={zone}
            onChange={(e) => setZone(e.target.value)}
            options={[
              { value: '', label: 'All zones' },
              ...zones.map((z) => ({ value: z, label: z })),
            ]}
          />
          <Select
            label="Vendor"
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            options={[
              { value: '', label: 'All vendors' },
              ...vendors.map((v) => ({ value: v, label: v })),
            ]}
          />
        </div>
        <Table
          rows={filtered}
          columns={cols}
          rowKey={(d) => d.device_id}
          density="compact"
          defaultSort={{ key: 'purdue', dir: 'desc' }}
        />
      </Card>
      <Card title="Purdue model coverage" subtitle="Device count per Purdue level">
        <ul className="iris-purdue-list">
          {PURDUE_LEVELS.map((p) => {
            const count = otDevices.filter((d) => d.purdue_level === p.level).length;
            return (
              <li key={p.level}>
                <div className="iris-purdue-name mono">L{p.level}</div>
                <div>
                  <div>{p.name.replace(`L${p.level} - `, '')}</div>
                  <div className="caption">{p.description}</div>
                </div>
                <div className="iris-purdue-count mono">{count}</div>
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
}

function BaselinesPanel() {
  const baselines = useData((s) => s.baselines);
  return (
    <div className="iris-ot-grid">
      {baselines.map((b) => (
        <Card
          key={`${b.asset_id}-${b.zone}`}
          title={`${b.asset_id}`}
          subtitle={`Zone ${b.zone}`}
          accent={
            !b.learning_complete
              ? 'var(--status-degraded)'
              : b.anomalies_24h > 0
                ? 'var(--status-degraded)'
                : 'var(--status-operational)'
          }
        >
          <KeyValueGrid
            cols={2}
            items={[
              { label: 'Observed pairs', value: b.observed_pairs, mono: true },
              { label: 'Protocols seen', value: b.observed_protocols, mono: true },
              {
                label: 'Learning',
                value: b.learning_complete ? (
                  <Badge color="var(--status-operational)">Complete</Badge>
                ) : (
                  <Badge color="var(--status-degraded)">In progress</Badge>
                ),
              },
              {
                label: 'Anomalies 24h',
                value: b.anomalies_24h,
                mono: true,
              },
              {
                label: 'Started',
                value: fmtUTC(b.learning_started),
                mono: true,
              },
            ]}
          />
        </Card>
      ))}
    </div>
  );
}

function PoliciesPanel() {
  const policies = useData((s) => s.policies);
  const cols: Column<PolicyRule>[] = [
    {
      key: 'rule_id',
      header: 'ID',
      width: '8%',
      mono: true,
      sortValue: (p) => p.rule_id,
      render: (p) => p.rule_id,
    },
    {
      key: 'name',
      header: 'Rule',
      sortValue: (p) => p.name,
      render: (p) => (
        <div>
          <div>{p.name}</div>
          <div className="caption mono">
            {p.zone_from} → {p.zone_to}
            {p.protocol ? ` · ${p.protocol}` : ''}
            {p.port ? ` · :${p.port}` : ''}
            {p.time_window ? ` · ${p.time_window.start}-${p.time_window.end}` : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'mode',
      header: 'Mode',
      width: '8%',
      sortValue: (p) => p.mode,
      render: (p) => <Badge color="var(--text-secondary)">{p.mode}</Badge>,
    },
    {
      key: 'action',
      header: 'Action',
      width: '8%',
      sortValue: (p) => p.action,
      render: (p) => (
        <Badge
          color={
            p.action === 'block' || p.action === 'kill'
              ? 'var(--status-critical)'
              : p.action === 'quarantine'
                ? 'var(--status-degraded)'
                : 'var(--status-info)'
          }
        >
          {p.action}
        </Badge>
      ),
    },
    {
      key: 'enabled',
      header: 'Enabled',
      width: '7%',
      align: 'center',
      render: (p) =>
        p.enabled ? (
          <Badge color="var(--status-operational)">on</Badge>
        ) : (
          <Badge color="var(--text-secondary)">off</Badge>
        ),
    },
    {
      key: 'hits',
      header: 'Hits',
      width: '8%',
      mono: true,
      align: 'right',
      sortValue: (p) => p.hits,
      render: (p) => fmtNumber(p.hits, { maximumFractionDigits: 0 }),
    },
    {
      key: 'created',
      header: 'Created',
      width: '12%',
      sortValue: (p) => p.created_at,
      render: (p) => <span className="caption">{p.created_by}</span>,
    },
  ];
  return (
    <Card>
      <Table
        rows={policies}
        columns={cols}
        rowKey={(p) => p.rule_id}
        density="compact"
        defaultSort={{ key: 'hits', dir: 'desc' }}
      />
    </Card>
  );
}

function SensorsPanel() {
  const sensors = useData((s) => s.sensors);
  return (
    <div className="iris-ot-grid">
      {sensors.map((s) => (
        <Card
          key={s.sensor_id}
          title={s.name}
          subtitle={`${s.site} · ${s.deployment_mode} · ${s.form_factor}`}
          accent={
            s.status === 'online'
              ? 'var(--status-operational)'
              : s.status === 'degraded'
                ? 'var(--status-degraded)'
                : 'var(--status-critical)'
          }
        >
          <KeyValueGrid
            cols={2}
            items={[
              { label: 'Sensor ID', value: s.sensor_id, mono: true },
              { label: 'Asset', value: s.asset_id, mono: true },
              { label: 'Throughput', value: `${s.throughput_mbps} Mbps`, mono: true },
              { label: 'Uptime', value: `${s.uptime_pct.toFixed(3)}%`, mono: true },
              { label: 'Last heartbeat', value: fmtRelative(s.last_heartbeat) },
            ]}
          />
        </Card>
      ))}
    </div>
  );
}

function FusionPanel() {
  return (
    <div className="iris-ot-grid">
      {JDL_LEVELS.map((l) => (
        <Card
          key={l.level}
          title={l.label}
          subtitle={l.description}
          accent={
            l.health === 'green'
              ? 'var(--status-operational)'
              : l.health === 'amber'
                ? 'var(--status-degraded)'
                : 'var(--status-critical)'
          }
        >
          <KeyValueGrid
            cols={2}
            items={[
              { label: 'Throughput', value: `${l.throughput_per_min}/min`, mono: true },
              { label: 'Health', value: <Badge color={
                l.health === 'green' ? 'var(--status-operational)' : l.health === 'amber' ? 'var(--status-degraded)' : 'var(--status-critical)'
              }>{l.health}</Badge> },
            ]}
          />
          <p style={{ marginTop: 8, color: 'var(--text-secondary)' }}>{l.current_activity}</p>
        </Card>
      ))}
    </div>
  );
}
